import { useMutation } from "@tanstack/react-query";

import {
  downloadCostAnalysisAdHoc,
  downloadCostAnalysisForTask,
  downloadQuoteAdHoc,
  downloadQuoteForTask,
  downloadRoiReportAdHoc,
  downloadRoiReportForTask,
  downloadSupportResponseForTask,
  type AdHocQuoteRenderRequest,
  type CostAnalysisRequest,
  type CostAnalysisTaskRequest,
  type DocumentFormat,
  type QuoteRenderRequest,
  type RoiReportRequest,
  type RoiReportTaskRequest,
} from "@/api/quoteDocuments";

// --- Streamed downloads (side-effecting mutations) --------------------------

export function useDownloadCostAnalysis() {
  return useMutation<
    void,
    unknown,
    | { taskId: string; body?: CostAnalysisTaskRequest }
    | { adHoc: CostAnalysisRequest }
  >({
    mutationFn: (vars) =>
      "adHoc" in vars
        ? downloadCostAnalysisAdHoc(vars.adHoc)
        : downloadCostAnalysisForTask(vars.taskId, vars.body),
  });
}

export function useDownloadRoiReport() {
  return useMutation<
    void,
    unknown,
    { taskId: string; body?: RoiReportTaskRequest } | { adHoc: RoiReportRequest }
  >({
    mutationFn: (vars) =>
      "adHoc" in vars
        ? downloadRoiReportAdHoc(vars.adHoc)
        : downloadRoiReportForTask(vars.taskId, vars.body),
  });
}

export function useDownloadAdHocQuote() {
  return useMutation<void, unknown, AdHocQuoteRenderRequest>({
    mutationFn: (body) => downloadQuoteAdHoc(body),
  });
}

// --- Task-bound quotation download (streamed, approval-gated) ---------------

export function useDownloadTaskQuote() {
  return useMutation<void, unknown, { taskId: string; body?: QuoteRenderRequest }>({
    mutationFn: ({ taskId, body }) => downloadQuoteForTask(taskId, body),
  });
}

// --- CR-005 support-response document (streamed) ----------------------------

export function useDownloadSupportResponse() {
  return useMutation<void, unknown, { taskId: string; format: DocumentFormat }>({
    mutationFn: ({ taskId, format }) =>
      downloadSupportResponseForTask(taskId, format),
  });
}
